<template>
    <div class="compass-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 class="h5 mb-0">Your Open Orders</h3>
            <button class="compass-btn compass-btn--sm compass-btn--outline" @click="fetchOrders" :disabled="loading">
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            </button>
        </div>

        <!-- Not Connected -->
        <div v-if="!isConnected" class="text-center py-4 text-muted">
            <i class="fas fa-wallet fa-2x mb-2 d-block"></i>
            <p class="small mb-0">Connect your wallet to view open orders</p>
        </div>

        <!-- Loading -->
        <div v-else-if="loading && orders.length === 0" class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x text-muted mb-2 d-block"></i>
            <p class="small text-muted mb-0">Loading orders...</p>
        </div>

        <!-- Empty -->
        <div v-else-if="orders.length === 0" class="text-center py-4 text-muted">
            <i class="fas fa-clipboard-list fa-2x mb-2 d-block"></i>
            <p class="small mb-0">No open orders</p>
        </div>

        <!-- Orders Table -->
        <div v-else class="table-mobile-scroll">
            <table class="compass-table">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Pair</th>
                        <th class="text-end">Remaining</th>
                        <th class="text-end">Value</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="order in orders" :key="order.orderKey">
                        <td>
                            <span class="compass-badge" :class="order.type === 'dca' ? 'compass-badge--purple' : 'compass-badge--outline'">
                                {{ order.type === 'dca' ? 'DCA' : 'Limit' }}
                            </span>
                        </td>
                        <td>
                            <span class="small">{{ order.inputSymbol || truncateAddress(order.inputMint) }}</span>
                            <i class="fas fa-arrow-right mx-1 text-muted" style="font-size: 0.7rem;"></i>
                            <span class="small">{{ order.outputSymbol || truncateAddress(order.outputMint) }}</span>
                        </td>
                        <td class="text-end mono-num small">
                            {{ formatAmount(order.remainingInputAmount, order.inputDecimals) }}
                        </td>
                        <td class="text-end mono-num small">
                            <span v-if="order.valueUsd !== null">${{ order.valueUsd.toFixed(2) }}</span>
                            <span v-else class="text-muted">—</span>
                        </td>
                        <td class="text-end">
                            <button
                                class="compass-btn compass-btn--sm compass-btn--outline text-danger"
                                @click="cancelOrder(order)"
                                :disabled="cancelling === order.orderKey"
                                title="Cancel order"
                            >
                                <i class="fas" :class="cancelling === order.orderKey ? 'fa-spinner fa-spin' : 'fa-times'"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Cancel Error -->
        <div class="alert alert-danger small py-2 mt-2" v-if="error">
            {{ error }}
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';

export default {
    name: 'OpenOrders',

    props: {
        filterMint: { type: String, default: '' },
    },

    data() {
        return {
            orders: [],
            loading: false,
            cancelling: null,
            error: null,
        };
    },

    computed: {
        isConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        walletAddress() {
            return window.wallet?.publicKey?.value?.toBase58() || null;
        },
    },

    watch: {
        isConnected(val) {
            if (val) this.fetchOrders();
        },
    },

    mounted() {
        if (this.isConnected) {
            this.fetchOrders();
        }
    },

    methods: {
        truncateAddress(addr) {
            if (!addr) return '';
            return addr.slice(0, 4) + '...' + addr.slice(-4);
        },

        formatAmount(amount, decimals) {
            if (amount == null) return '—';
            const val = typeof amount === 'number' ? amount : Number(amount);
            if (val >= 1) return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
            return val.toPrecision(4);
        },

        async fetchOrders() {
            if (!this.walletAddress) return;
            this.loading = true;
            this.error = null;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                let orders = await sdk.order.getOpenOrders(this.walletAddress);

                if (this.filterMint) {
                    orders = orders.filter(o =>
                        o.inputMint === this.filterMint || o.outputMint === this.filterMint
                    );
                }

                this.orders = orders;
            } catch (e) {
                this.error = e.message || 'Failed to load orders';
                console.error('[OpenOrders] fetchOrders error:', e);
            } finally {
                this.loading = false;
            }
        },

        async cancelOrder(order) {
            this.cancelling = order.orderKey;
            this.error = null;

            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();

                if (order.type === 'dca') {
                    await sdk.order.cancelDca(order.orderKey, 'default');
                } else {
                    await sdk.order.cancelLimit(order.orderKey, 'default');
                }

                this.orders = this.orders.filter(o => o.orderKey !== order.orderKey);
            } catch (e) {
                this.error = e.message || 'Failed to cancel order';
                console.error('[OpenOrders] cancelOrder error:', e);
            } finally {
                this.cancelling = null;
            }
        },
    },
};
</script>
